(function () {
  if (window.__discountBadgesInit) return;
  window.__discountBadgesInit = true;

  // ============================================================
  // CONFIGURATION — update these values for each deployment
  // ============================================================

  const CONFIG = {
    // Storefront API credentials
    STOREFRONT_TOKEN: '3db91ab260e51eff5c4d4f05b392910f',
    STOREFRONT_URL: 'https://fullhousemodern.com/api/2024-01/graphql.json',

    // Trade discount settings
    TRADE_DISCOUNT_RATE: 0.10,           // 10% — decimal format
    TRADE_DISCOUNT_TITLE: 'TRADE DISCOUNT',
    TRADE_CUSTOMER_TAG: 'Trade',         // customer tag that grants trade access
    TRADE_COLLECTION_HANDLE: 'trade-discount', // collection handle for trade-eligible products

    // CSS selectors — update to match the theme's DOM structure
    PRICE_SELECTOR: '.product-details .tww-price__current [data-price]',
    VARIANT_SELECTION_SELECTOR: 'variant-selection[data-variant-selection]',
    VARIANT_SELECT_SELECTOR: 'select[name="id"]',

    // Badge colours
    TRADE_BADGE_BG: '#1a1a1a',
    SALE_BADGE_BG: '#c00',
    BADGE_TEXT_COLOR: '#fff',

    // Currency fallback (used if Shopify.formatMoney is unavailable)
    CURRENCY_LOCALE: 'en-CA',
    CURRENCY_CODE: 'CAD',

    // Debounce timing (ms)
    RUN_CHECK_DEBOUNCE: 300,
    OBSERVER_DEBOUNCE: 300,
    VARIANT_CHANGE_DELAY: 100,
  };

  // ============================================================
  // CUSTOMER HELPERS
  // ============================================================

  function isTradeCustomer(card) {
    const customer = window.Shopify?.customer;
    if (!customer) {
      console.log('[Discount] No customer logged in.');
      return false;
    }

    const tags = Array.isArray(customer.tags)
      ? customer.tags
      : (customer.tags ?? '').split(',').map(t => t.trim());

    if (!tags.includes(CONFIG.TRADE_CUSTOMER_TAG)) {
      console.log('[Discount] Customer does not have Trade tag.');
      return false;
    }

    if (card) {
      const collections = (card.dataset.collections ?? '').split(',').map(c => c.trim());
      if (!collections.includes(CONFIG.TRADE_COLLECTION_HANDLE)) {
        console.log('[Discount] Product is not in Trade Discount collection.');
        return false;
      }
    }

    console.log('[Discount] Trade customer confirmed for this product.');
    return true;
  }

  // ============================================================
  // CURRENCY FORMATTER
  // ============================================================

  function formatPrice(amount) {
    const moneyFormat =
      window.theme?.moneyFormat ||
      window.Shopify?.money_format ||
      document.querySelector('[data-money-format]')?.dataset.moneyFormat ||
      '${{amount}}';
    const rounded = Math.ceil(amount);
    const stripCents = (str) => str.replace(/\.00$/, '');
    if (window.Shopify?.formatMoney) {
      try {
        return stripCents(window.Shopify.formatMoney(rounded * 100, moneyFormat));
      } catch (e) {
        console.warn('[Discount] formatMoney failed, using fallback:', e);
      }
    }
    return stripCents(
      new Intl.NumberFormat(CONFIG.CURRENCY_LOCALE, {
        style: 'currency',
        currency: CONFIG.CURRENCY_CODE,
      }).format(rounded)
    );
  }

  // ============================================================
  // UI HELPERS — product page
  // ============================================================

  function removeDiscountUI() {
    const existing = document.getElementById('tww-discount-ui');
    if (existing) {
      existing.remove();
      console.log('[Discount] Removed existing discount UI.');
    }
    const priceEl = document.querySelector(CONFIG.PRICE_SELECTOR);
    if (priceEl) {
      priceEl.style.display = '';
      priceEl.style.textDecoration = '';
      priceEl.style.color = '';
    }
  }

  function showDiscountPrice(originalPrice, discountedAmount, discountTitle) {
    removeDiscountUI();

    const priceEl = document.querySelector(CONFIG.PRICE_SELECTOR);
    if (!priceEl) {
      console.warn('[Discount] Could not find price element to update UI.');
      return;
    }

    const original = parseFloat(originalPrice);
    const saving = parseFloat(discountedAmount);
    const finalPrice = original - saving;
    const isTrade = discountTitle === CONFIG.TRADE_DISCOUNT_TITLE;

    console.log(`[Discount] Showing discount UI: ${discountTitle} | Original: $${original.toFixed(2)} | Saving: $${saving.toFixed(2)} | Final: $${finalPrice.toFixed(2)}`);

    priceEl.style.display = 'none';

    const wrapper = document.createElement('div');
    wrapper.id = 'tww-discount-ui';
    wrapper.style.cssText = 'display:flex; align-items:start; gap:10px;';
    wrapper.innerHTML = `
      <div class="product-page-badge product-badge--${isTrade ? 'trade' : 'sale'}"
           style="position:relative; display:inline-flex; padding:2px 8px; border-radius:3px; font-size:0.75em; margin-top:4px; background:${isTrade ? CONFIG.TRADE_BADGE_BG : CONFIG.SALE_BADGE_BG}; color:${CONFIG.BADGE_TEXT_COLOR};">
        ${isTrade ? 'Trade' : 'Sale'}
      </div>
      <div>
        <span style="text-decoration:line-through; color:#999; font-size:0.9em;">${formatPrice(original)}</span>
        <span style="color:${CONFIG.SALE_BADGE_BG};">${formatPrice(finalPrice)}</span>
        <div style="font-size:10px; color:#555;">${discountTitle} (−${formatPrice(saving)})</div>
      </div>
    `;

    priceEl.insertAdjacentElement('afterend', wrapper);
    console.log('[Discount] Discount UI injected.');
  }

  // ============================================================
  // STOREFRONT API — product page
  // ============================================================

  let checkInFlight = false;

  async function checkDiscountEligibility(variantId, variantPrice) {
    if (checkInFlight) {
      console.log('[Discount] Check already in flight — skipping.');
      return;
    }
    checkInFlight = true;

    const gid = `gid://shopify/ProductVariant/${variantId}`;
    console.log(`[Discount] Checking variant: ${gid} at $${variantPrice}`);

    const query = `
      mutation cartCreate($input: CartInput!) {
        cartCreate(input: $input) {
          cart {
            lines(first: 1) {
              edges {
                node {
                  discountAllocations {
                    discountedAmount { amount currencyCode }
                    ... on CartAutomaticDiscountAllocation {
                      title
                      discountedAmount { amount currencyCode }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    try {
      const res = await fetch(CONFIG.STOREFRONT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': CONFIG.STOREFRONT_TOKEN,
        },
        body: JSON.stringify({
          query,
          variables: { input: { lines: [{ quantity: 1, merchandiseId: gid }] } },
        }),
      });

      const data = await res.json();
      console.log('[Discount] Raw API response:', JSON.stringify(data, null, 2));

      const lines = data?.data?.cartCreate?.cart?.lines?.edges;
      const allocations = lines?.[0]?.node?.discountAllocations ?? [];

      const bestCartDiscount = allocations.length > 0
        ? allocations.reduce((best, current) =>
            parseFloat(current.discountedAmount.amount) > parseFloat(best.discountedAmount.amount)
              ? current : best)
        : null;

      const bestCartAmount = bestCartDiscount
        ? parseFloat(bestCartDiscount.discountedAmount.amount)
        : 0;
      const productWrapper = document.querySelector('[data-collections]');
      const tradeAmount = isTradeCustomer(productWrapper)
        ? parseFloat(variantPrice) * CONFIG.TRADE_DISCOUNT_RATE
        : 0;

      console.log(`[Discount] Best cart: $${bestCartAmount.toFixed(2)} | Trade: $${tradeAmount.toFixed(2)}`);

      if (bestCartAmount === 0 && tradeAmount === 0) {
        return removeDiscountUI();
      }

      if (tradeAmount > bestCartAmount) {
        showDiscountPrice(variantPrice, tradeAmount, CONFIG.TRADE_DISCOUNT_TITLE);
      } else {
        showDiscountPrice(variantPrice, bestCartAmount, bestCartDiscount.title || 'Discount');
      }

    } catch (err) {
      console.error('[Discount] API call failed:', err.name, err.message);
      removeDiscountUI();
    } finally {
      checkInFlight = false;
    }
  }

  // ============================================================
  // BADGE HELPERS — grid/collection pages
  // ============================================================

  function clearBadgeChecked() {
    document.querySelectorAll('[data-badge-checked]').forEach(card => {
      card.removeAttribute('data-badge-checked');
    });
    console.log('[Badge] Cleared data-badge-checked from all cards.');
  }

  // ============================================================
  // STOREFRONT API — product grid badges
  // ============================================================

  async function checkGridDiscounts() {
    clearBadgeChecked();

    const cards = Array.from(document.querySelectorAll('[data-product-card][data-variant-id]'));
    if (cards.length === 0) {
      console.log('[Badge] No product cards found.');
      return;
    }

    console.log(`[Badge] Checking ${cards.length} product cards.`);

    const lines = cards.map(card => ({
      quantity: 1,
      merchandiseId: `gid://shopify/ProductVariant/${card.dataset.variantId}`,
    }));

    const query = `
      mutation cartCreate($input: CartInput!) {
        cartCreate(input: $input) {
          cart {
            lines(first: 50) {
              edges {
                node {
                  merchandise {
                    ... on ProductVariant { id }
                  }
                  discountAllocations {
                    discountedAmount { amount }
                    ... on CartAutomaticDiscountAllocation {
                      title
                      discountedAmount { amount }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    try {
      const res = await fetch(CONFIG.STOREFRONT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': CONFIG.STOREFRONT_TOKEN,
        },
        body: JSON.stringify({ query, variables: { input: { lines } } }),
      });

      const data = await res.json();
      const cartLines = data?.data?.cartCreate?.cart?.lines?.edges ?? [];
      console.log(`[Badge] Got ${cartLines.length} cart lines back.`);

      cartLines.forEach(({ node }) => {
        const variantId = node.merchandise.id.split('/').pop();
        const card = document.querySelector(`[data-product-card][data-variant-id="${variantId}"]`);
        if (!card) return;

        const allocations = node.discountAllocations ?? [];

        const bestSale = allocations
          .filter(a => a.title?.toLowerCase().includes('sale'))
          .reduce((best, current) =>
            !best || parseFloat(current.discountedAmount.amount) > parseFloat(best.discountedAmount.amount)
              ? current : best
          , null);

        const tradeAmount = isTradeCustomer(card)
          ? parseFloat(card.dataset.price ?? 0) * CONFIG.TRADE_DISCOUNT_RATE
          : 0;
        const saleAmount = bestSale ? parseFloat(bestSale.discountedAmount.amount) : 0;

        card.querySelector('.product-badge--sale-js')?.remove();
        card.querySelector('.product-badge--trade-js')?.remove();

        if (saleAmount > 0 && saleAmount >= tradeAmount) {
          const badge = document.createElement('span');
          badge.className = 'product-badge product-badge--sale product-badge--sale-js';
          badge.textContent = 'Sale';
          card.appendChild(badge);
          console.log(`[Badge] Sale badge added for variant ${variantId}`);
        } else if (tradeAmount > 0) {
          const badge = document.createElement('span');
          badge.className = 'product-badge product-badge--trade product-badge--trade-js';
          badge.textContent = 'Trade';
          card.appendChild(badge);
          console.log(`[Badge] Trade badge added for variant ${variantId}`);
        } else {
          console.log(`[Badge] No badge for variant ${variantId} — no eligible discounts.`);
        }

        card.setAttribute('data-badge-checked', 'true');
      });

    } catch (err) {
      console.error('[Badge] Storefront API call failed:', err);
    }
  }

  // ============================================================
  // MUTATION OBSERVER — late-rendered cards (recently viewed, etc.)
  // ============================================================

  function watchForNewCards() {
    let observerTimer = null;
    let isChecking = false;

    const observer = new MutationObserver(() => {
      if (isChecking) return;

      const newCards = document.querySelectorAll(
        '[data-product-card][data-variant-id]:not([data-badge-checked])'
      );
      if (newCards.length === 0) return;

      clearTimeout(observerTimer);
      observerTimer = setTimeout(async () => {
        console.log('[Badge] New cards detected — running badge check.');
        isChecking = true;
        await checkGridDiscounts();
        isChecking = false;
      }, CONFIG.OBSERVER_DEBOUNCE);
    });

    observer.observe(document.body, { childList: true, subtree: true });
    console.log('[Badge] MutationObserver watching for new product cards.');
  }

  // ============================================================
  // VARIANT DETECTION — product page
  // ============================================================

  function getSelectedVariantId() {
    const variantSelection = document.querySelector(CONFIG.VARIANT_SELECTION_SELECTOR);
    if (variantSelection) {
      const variantId = variantSelection.getAttribute('variant');
      console.log(`[Discount] Variant from variant-selection: ${variantId}`);
      return variantId;
    }
    const select = document.querySelector(CONFIG.VARIANT_SELECT_SELECTOR);
    if (select && select.value !== 'not-selected') {
      console.log(`[Discount] Variant from select: ${select.value}`);
      return select.value;
    }
    console.warn('[Discount] Could not find variant ID.');
    return null;
  }

  function getVariantPrice() {
    const select = document.querySelector(CONFIG.VARIANT_SELECT_SELECTOR);
    if (select) {
      const selectedOption = select.options[select.selectedIndex];
      if (selectedOption) {
        const match = selectedOption.textContent.match(/\$([\d,]+)/);
        if (match) return parseFloat(match[1].replace(/,/g, ''));
      }
    }
    const priceEl = document.querySelector(CONFIG.PRICE_SELECTOR);
    if (priceEl) return parseFloat(priceEl.textContent.replace(/[^0-9.]/g, ''));
    console.warn('[Discount] Could not find price.');
    return 0;
  }

  // ============================================================
  // MAIN CHECK — product page, debounced
  // ============================================================

  let debounceTimer = null;

  function runCheck() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      console.log('[Discount] Running discount eligibility check...');
      const variantId = getSelectedVariantId();
      if (!variantId) return;
      const price = getVariantPrice();
      if (!price || price <= 0) {
        console.warn('[Discount] Price is 0 or unavailable — skipping check.');
        return;
      }
      checkDiscountEligibility(variantId, price);
    }, CONFIG.RUN_CHECK_DEBOUNCE);
  }

  // ============================================================
  // INIT
  // ============================================================

  function init() {
    const isProductPage = document.querySelector(
      `${CONFIG.VARIANT_SELECTION_SELECTOR}, ${CONFIG.VARIANT_SELECT_SELECTOR}`
    );
    const isGridPage = document.querySelector('[data-product-card][data-variant-id]');

    if (isProductPage) {
      console.log('[Discount] Product page — starting variant check.');
      runCheck();

      const variantSelection = document.querySelector(CONFIG.VARIANT_SELECTION_SELECTOR);
      if (variantSelection) {
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.attributeName === 'variant') {
              console.log('[Discount] variant-selection changed — re-checking...');
              setTimeout(runCheck, CONFIG.VARIANT_CHANGE_DELAY);
            }
          });
        });
        observer.observe(variantSelection, { attributes: true });
        console.log('[Discount] MutationObserver attached to variant-selection.');
      }
    }

    if (isGridPage) {
      console.log('[Badge] Grid page — starting badge check.');
      checkGridDiscounts();
    }

    if (isGridPage || isProductPage) {
      watchForNewCards();
    }
  }

  document.addEventListener('DOMContentLoaded', init);

  // ============================================================
  // FALLBACK VARIANT CHANGE LISTENERS
  // ============================================================

  document.addEventListener('change', (e) => {
    if (e.target.matches(`[name="id"], .product-form__input input, ${CONFIG.VARIANT_SELECT_SELECTOR}`)) {
      console.log('[Discount] Variant input changed — re-checking...');
      setTimeout(runCheck, CONFIG.VARIANT_CHANGE_DELAY);
    }
  });

  document.addEventListener('variant:changed', () => runCheck());
  document.addEventListener('variantChange', () => runCheck());

  // ============================================================
  // AJAX / TURBO NAVIGATION SUPPORT
  // ============================================================

  ['page:load', 'page:change', 'turbo:load', 'turbolinks:load'].forEach(evt => {
    document.addEventListener(evt, () => {
      console.log(`[Badge] ${evt} fired — re-running init.`);
      init();
    });
  });

})();
